import { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';
import { Pen, Trash2, CheckCircle2, Shield, AlertCircle } from 'lucide-react';
import type { WizardDaten } from '@/lib/mietwagen-wizard-validation';

interface Props {
  daten: WizardDaten;
  onChange: (updated: Partial<WizardDaten>) => void;
  onSigVertragChange: (dataUrl: string) => void;
  onSigAbtretungChange: (dataUrl: string) => void;
  onSigUebergabeChange: (dataUrl: string) => void;
  sigVertrag: string;
  sigAbtretung: string;
  sigUebergabe: string;
}

export default function Step6Unterschrift({
  daten,
  onChange,
  onSigVertragChange,
  onSigAbtretungChange,
  onSigUebergabeChange,
  sigVertrag,
  sigAbtretung,
  sigUebergabe,
}: Props) {
  const set = (field: keyof WizardDaten, value: unknown) => onChange({ [field]: value });

  const sigVertragRef = useRef<HTMLCanvasElement | null>(null);
  const sigAbtretungRef = useRef<HTMLCanvasElement | null>(null);
  const sigUebergabeRef = useRef<HTMLCanvasElement | null>(null);
  const padVertragRef = useRef<SignaturePad | null>(null);
  const padAbtretungRef = useRef<SignaturePad | null>(null);
  const padUebergabeRef = useRef<SignaturePad | null>(null);

  const [unterschriftDatum] = useState(() =>
    new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
  );

  useEffect(() => {
    const initPad = (
      canvasRef: React.RefObject<HTMLCanvasElement | null>,
      padRef: React.MutableRefObject<SignaturePad | null>,
      onEnd: (dataUrl: string) => void
    ) => {
      if (!canvasRef.current) return;
      const canvas = canvasRef.current;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(ratio, ratio);

      const pad = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 0.5,
        maxWidth: 2.5,
      });
      pad.addEventListener('endStroke', () => {
        onEnd(pad.isEmpty() ? '' : pad.toDataURL('image/png'));
      });
      padRef.current = pad;
    };

    const t = setTimeout(() => {
      initPad(sigVertragRef, padVertragRef, onSigVertragChange);
      initPad(sigAbtretungRef, padAbtretungRef, onSigAbtretungChange);
      initPad(sigUebergabeRef, padUebergabeRef, onSigUebergabeChange);
    }, 100);

    return () => {
      clearTimeout(t);
      padVertragRef.current?.off();
      padAbtretungRef.current?.off();
      padUebergabeRef.current?.off();
    };
  }, [onSigVertragChange, onSigAbtretungChange, onSigUebergabeChange]);

  const allCheckboxes = daten.checkbox_mietbedingungen && daten.checkbox_abtretung && daten.checkbox_datenschutz;
  const allSigs = !!sigVertrag && !!sigAbtretung;

  return (
    <div className="space-y-4 p-5">
      {/* Checkboxen */}
      <div className="space-y-2">
        <CheckboxRow
          checked={daten.checkbox_mietbedingungen}
          onChange={v => set('checkbox_mietbedingungen', v)}
          label="Ich akzeptiere die Mietbedingungen und bestätige, dass alle Angaben korrekt sind."
        />
        <CheckboxRow
          checked={daten.checkbox_abtretung}
          onChange={v => set('checkbox_abtretung', v)}
          label="Ich stimme der Abtretung meiner Mietwagenkosten-Erstattungsansprüche an die LCS GmbH (MietGo) gem. §398 BGB zu."
        />
        <CheckboxRow
          checked={daten.checkbox_datenschutz}
          onChange={v => set('checkbox_datenschutz', v)}
          label="Ich akzeptiere die Datenschutzbestimmungen und bin mit der Verarbeitung meiner Daten einverstanden."
        />
      </div>

      {/* Unterschrift 1: Mietvertrag */}
      <SignatureBlock
        title="Unterschrift Mietvertrag"
        subtitle={`${daten.mieter_vorname} ${daten.mieter_nachname}`}
        canvasRef={sigVertragRef}
        hasSig={!!sigVertrag}
        onClear={() => { padVertragRef.current?.clear(); onSigVertragChange(''); }}
        datum={unterschriftDatum}
      />

      {/* Unterschrift 2: Abtretung */}
      <SignatureBlock
        title="Unterschrift Abtretungserklärung"
        subtitle={`${daten.mieter_vorname} ${daten.mieter_nachname} (Zedent)`}
        canvasRef={sigAbtretungRef}
        hasSig={!!sigAbtretung}
        onClear={() => { padAbtretungRef.current?.clear(); onSigAbtretungChange(''); }}
        datum={unterschriftDatum}
      />

      {/* Unterschrift 3: Übergabe (optional) */}
      <SignatureBlock
        title="Unterschrift Übergabeprotokoll (optional)"
        subtitle={`${daten.mieter_vorname} ${daten.mieter_nachname}`}
        canvasRef={sigUebergabeRef}
        hasSig={!!sigUebergabe}
        onClear={() => { padUebergabeRef.current?.clear(); onSigUebergabeChange(''); }}
        datum={unterschriftDatum}
        optional
      />

      {/* Rechtlicher Hinweis */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <Shield className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-blue-700 leading-relaxed">
          <strong>Rechtlicher Hinweis:</strong> Ihre digitale Unterschrift wird mit Zeitstempel gespeichert und hat
          die gleiche Wirksamkeit wie eine handschriftliche Unterschrift. Das unterzeichnete Dokument wird als PDF
          in Ihrer Akte gespeichert.
        </p>
      </div>

      {/* Validierungs-Hinweis */}
      {(!allCheckboxes || !allSigs) && (allCheckboxes || !!sigVertrag || !!sigAbtretung) && (
        <div className="flex items-center gap-2 justify-center">
          <AlertCircle className="w-3 h-3 text-amber-500 flex-shrink-0" />
          <p className="text-[11px] text-amber-600">
            {!allSigs
              ? 'Bitte unterschreiben Sie Mietvertrag und Abtretungserklärung'
              : 'Bitte akzeptieren Sie alle Bedingungen'}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Helper Components ──

function CheckboxRow({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={[
        'w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all',
        checked ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300',
      ].join(' ')}
    >
      <div className={[
        'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all',
        checked ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300',
      ].join(' ')}>
        {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
      </div>
      <span className="text-[11px] text-gray-700 leading-relaxed">{label}</span>
    </button>
  );
}

function SignatureBlock({
  title, subtitle, canvasRef, hasSig, onClear, datum, optional,
}: {
  title: string;
  subtitle: string;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  hasSig: boolean;
  onClear: () => void;
  datum: string;
  optional?: boolean;
}) {
  return (
    <div className={['rounded-2xl border-2 shadow-sm overflow-hidden', optional ? 'border-gray-100' : 'border-gray-200'].join(' ')}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Pen className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-[12px] font-bold text-gray-700">{title}</span>
          {optional && <span className="text-[9px] text-gray-400">(optional)</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">{datum}</span>
          {hasSig && (
            <button
              type="button"
              onClick={onClear}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-[10px] font-semibold transition"
            >
              <Trash2 className="w-3 h-3" /> Löschen
            </button>
          )}
        </div>
      </div>
      <div className="relative">
        <canvas ref={canvasRef} className="w-full touch-none" style={{ height: '120px', cursor: 'crosshair' }} />
        {!hasSig && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-[13px] text-gray-300 font-medium">Hier unterschreiben</p>
          </div>
        )}
      </div>
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
        <p className="text-[10px] text-gray-400">{subtitle}</p>
      </div>
    </div>
  );
}
